import {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import AsyncStorage from '@react-native-async-storage/async-storage'
import useSentry from '@hooks/useSentry'
import { useSnackbar, SnackbarThemes } from '@context/SnackbarContext'

interface Serialization<T> {
  serialize: (value: T) => string
  deserialize: (value: string) => T
}

type ReturnType<T> = [T, Dispatch<SetStateAction<T>>, boolean]

const useAsyncStorageState = <T extends unknown>(
  key: string,
  defaultValue: T,
  options: Serialization<T> = {
    serialize: JSON.stringify,
    deserialize: JSON.parse,
  },
): ReturnType<T> => {
  // AsyncStorage.clear()
  const { serialize, deserialize } = options
  const [state, setState] = useState<T>(
    typeof defaultValue === 'function' ? defaultValue() : defaultValue,
  )
  const [loading, setLoading] = useState<boolean>(true)

  /**
   * Sentry to log errors, and snackbar to show a message to the user
   *  */
  const { captureException } = useSentry()
  const { setSnackbarMessage } = useSnackbar()

  /**
   * Keep a reference to the old key in AsyncStorage,
   * to delete it if the key changes
   */
  const prevKeyRef = useRef(key)

  /**
   * First, we need to do lazy state initialization.
   * This is done only once, on the first execution
   * (hence the empty dependency array).
   */
  useEffect(() => {
    let mounted = true

    ;(async (): Promise<Maybe<T>> => {
      const valueInAsyncStorage = await AsyncStorage.getItem(key)
      if (valueInAsyncStorage) {
        return deserialize(valueInAsyncStorage)
      }
      return undefined
    })().then((data) => {
      if (data !== undefined) {
        if (mounted) {
          setState(data as T)
          setLoading(false)
        }
      }
    })

    return () => { mounted = false }
  }, [])

  /**
   * It is also necessary to set the key in AsyncStorage on change.
   * This also checks if the key has changed, in order to remove the old
   * one so that everything's neat and tidy.
   */
  useEffect(() => {
    // Check if key has changed, and remove previous key if necessary
    const prevKey = prevKeyRef.current
    if (prevKey !== key) {
      (async () => {
        try {
          await AsyncStorage.removeItem(prevKey)
          prevKeyRef.current = key
        } catch (error) {
          captureException(error)
          setSnackbarMessage('AsyncStorage failure', SnackbarThemes.error)
        }
      })()
    }

    // Set item in localStorage
    (async () => {
      try {
        await AsyncStorage.setItem(key, serialize(state as T))
      } catch (error) {
        captureException(error)
        setSnackbarMessage('AsyncStorage failure', SnackbarThemes.error)
      }
    })()
  }, [key, serialize, state])

  return [state, setState, loading]
}

export default useAsyncStorageState
